import { z } from 'zod';
import { gstRateSchema, hsnSacSchema } from '../money/gst.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { stockUnitSchema } from '../inventory/units.js';
import { isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * The price list: what the shop SELLS, named once, priced once.
 *
 * WHY THIS EXISTS WHEN THERE IS ALREADY A RATE CARD AND A SHELF. Those two answer different
 * questions, and neither answers this one. The rate card is what the shop PAYS a karigar for a
 * piece of work; stock is what the shop HOLDS and what it cost to buy. Between them there was
 * nowhere to write down "a blouse is ₹850" — so that number lived in whoever was at the counter,
 * got typed fresh onto every quotation, and drifted between two people quoting the same garment
 * on the same day.
 *
 * IT IS A CATALOGUE, NOT A LEDGER. Nothing here moves, is consumed, or has a quantity: a product
 * is a line a shop is prepared to sell at a stated price. "Bridal lehenga stitching" is sellable
 * without a single metre on the shelf, and tying every product to stock would make one of the two
 * lists wrong.
 *
 * THE ONE EXCEPTION IS GOODS SOLD OVER THE COUNTER. Fabric by the metre, a readymade blouse, a
 * packet of buttons — when those leave the shop on an order's product line, the shelf really is
 * lighter. So a product in a `STOCKABLE_PRODUCT_CATEGORIES` category MAY point at one stock
 * variant, and selling it writes a `sale` movement through the inventory module's one door. The
 * link is optional, it is refused on stitching, alteration and service lines, and the product
 * still holds no quantity of its own: the ledger stays the only place a count lives.
 *
 * IT DOES NOT REWRITE QUOTATIONS. A quotation line still carries its own frozen label and price,
 * for the reason `quotationLineSchema` gives: a price already sent to a customer must not change
 * because somebody edited the catalogue afterwards. A product is where a counter COPIES a price
 * from, never a pointer a sent document follows.
 */

/**
 * What a shop sells, in its own vocabulary.
 *
 * A closed list rather than a shop-defined one, matching `STOCK_CATEGORIES` next door: these are
 * the headings every tailoring business has, and a free list turns "stitching" and "Stitching"
 * into two rows nobody can report on. A shop that needs a finer split has `code` and the name.
 */
export const PRODUCT_CATEGORIES = [
  /** Making a garment from scratch — the shop's core line. */
  'stitching',
  /** Taking in, letting out, re-hemming. Priced quite differently from stitching. */
  'alteration',
  /** Cloth sold by the metre over the counter, as against cloth cut into a job. */
  'fabric',
  /** Bought in finished and sold on. */
  'readymade',
  /** Buttons, laces, falls, linings sold as an add-on. */
  'accessory',
  /** Anything charged for that is not a thing: fitting visits, express delivery, embroidery. */
  'service',
  'other',
] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];
export const productCategorySchema = z.enum(PRODUCT_CATEGORIES);

/** The categories that are physical goods, and so the only ones that may be linked to stock. */
export const STOCKABLE_PRODUCT_CATEGORIES = [
  'fabric',
  'readymade',
  'accessory',
] as const satisfies readonly ProductCategory[];

export function isStockableCategory(category: ProductCategory): boolean {
  return (STOCKABLE_PRODUCT_CATEGORIES as readonly ProductCategory[]).includes(category);
}

export const PRODUCT_NAME_MAX_LENGTH = 120;
export const PRODUCT_CODE_MAX_LENGTH = 40;
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 2000;

/** Past this the screen is a report, and the search box is the honest way through it. */
export const PRODUCT_LIST_LIMIT = 200;

/** The one field the media pipeline needs to file a product's picture; opaque to it, and ours. */
export const PRODUCT_MEDIA_ENTITY_TYPE = 'product';

/**
 * One product as every screen renders it.
 */
export const productSchema = z.object({
  id: objectIdSchema,

  /** What the counter calls it. Unique within the shop, compared case-insensitively. */
  name: z.string(),

  /**
   * The shop's own short reference — "BL-SLV", "SAR-FALL". Optional, and deliberately not
   * generated: a shop that already prints codes on its job cards must be able to keep them, and a
   * shop that has never used one should not be handed a number it then has to explain.
   */
  code: z.string().nullable(),

  category: productCategorySchema,

  /**
   * What it sells for, in paise. NULLABLE, and the nullability is the point: "we do bridal
   * blouses, price on the piece" is a real catalogue entry, and a shop forced to type 0 would be
   * publishing a free blouse to every screen that totals a list.
   */
  unitPricePaise: nonNegativePaiseSchema.nullable(),

  /** What `unitPricePaise` is per: a metre of fabric, a yard, or one piece. */
  unit: stockUnitSchema,

  /**
   * GST on this line when sold on an order, or `null` to take the shop's default. Per product
   * because fabric, readymade garments and stitching are taxed at different rates.
   */
  gstRatePercent: gstRateSchema.nullable(),

  /** The HSN code a GST invoice prints for it, or `null` to take the shop's default. */
  hsnCode: z.string().nullable(),

  /**
   * The stock variant a sale draws down, or both `null` for a product that is not tracked.
   * Only ever set on a `STOCKABLE_PRODUCT_CATEGORIES` product.
   */
  stockItemId: z.string().nullable(),
  stockVariantId: z.string().nullable(),

  /**
   * Whether the shop still sells it. Retiring is the ONLY way a product leaves the working list
   * without leaving the record — see `deleteProduct`, which is for the row typed by mistake.
   */
  active: z.boolean(),

  description: z.string().nullable(),

  /** A media id from the ordinary pipeline, filed under `product`. */
  imageMediaId: z.string().nullable(),

  /** Who looks after this line. Denormalised so a list of two hundred needs no second read. */
  ownerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Product = z.infer<typeof productSchema>;

/**
 * A page of the catalogue.
 *
 * `total` counts every product the filters match, `items` is what fits under `PRODUCT_LIST_LIMIT`.
 * They are separate for the reason a deal board column keeps them separate: a footer that totalled
 * only what it was sent would quietly understate a catalogue the moment it outgrew one page.
 */
export const productListSchema = z.object({
  items: z.array(productSchema),
  total: z.number().int().nonnegative(),
  /** How many live products the shop has retired — what the "show retired" toggle is worth. */
  retiredCount: z.number().int().nonnegative(),
});
export type ProductList = z.infer<typeof productListSchema>;

const productFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Name the product').max(PRODUCT_NAME_MAX_LENGTH),
  code: z.string().trim().max(PRODUCT_CODE_MAX_LENGTH).nullish(),
  category: productCategorySchema.default('stitching'),
  unitPricePaise: nonNegativePaiseSchema.nullish(),
  unit: stockUnitSchema.default('piece'),
  gstRatePercent: gstRateSchema.nullish(),
  hsnCode: hsnSacSchema.nullish(),
  stockItemId: objectIdSchema.nullish(),
  stockVariantId: z.string().min(1).nullish(),
  active: z.boolean().default(true),
  description: z.string().trim().max(PRODUCT_DESCRIPTION_MAX_LENGTH).nullish(),
  imageMediaId: objectIdSchema.nullish(),
  ownerUserId: objectIdSchema.nullish(),
});

/**
 * The stock link is all or nothing, and only on goods. The category half of the second rule can
 * only be checked here when the patch names the category; the service checks it against the
 * stored row otherwise.
 */
function checkStockLink(
  value: {
    category?: ProductCategory | undefined;
    stockItemId?: string | null | undefined;
    stockVariantId?: string | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const hasItem = value.stockItemId != null;
  const hasVariant = value.stockVariantId != null;
  if (hasItem !== hasVariant) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [hasItem ? 'stockVariantId' : 'stockItemId'],
      message: 'Pick both the stock item and its colour',
    });
  }
  if (hasItem && value.category !== undefined && !isStockableCategory(value.category)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['stockItemId'],
      message: 'Only fabric, readymade and accessory products can be linked to stock',
    });
  }
}

export const createProductSchema = productFieldsSchema.superRefine(checkStockLink);
export type CreateProductInput = z.input<typeof createProductSchema>;

/**
 * Correcting one. Every field, including `active` — retiring a line IS an edit, not an event, and
 * a shop that stops doing saree falls in March may well start again in November.
 */
export const updateProductSchema = productFieldsSchema
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' })
  .superRefine(checkStockLink);
export type UpdateProductInput = z.input<typeof updateProductSchema>;

export const productListQuerySchema = z.object({
  /** Free text over the name and the code — the two things somebody has in their head. */
  q: z.string().trim().max(120).optional(),
  category: productCategorySchema.optional(),
  /**
   * Retired lines are hidden by default, so the everyday list is what the shop actually sells.
   * The catalogue screen asks for them explicitly.
   */
  includeRetired: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .default(false),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;
